import {
  ttsModelsSupportedV3,
  ttsModelsSupportedV2nV3,
  ttsModelsSupportedV2nV3WithTurboV2_5,
  TTSModels,
} from "./models";

export type LanguageOrAccent = {
  asr?: boolean;
  label: string;
  /**
   * Optional label for user-facing pickers (speech/TTS/STT demos). Use when the
   * presentation label should differ from `label` — `label` doubles as a typed
   * i18n message key and a CMS lookup key, so it can't be changed freely.
   */
  displayLabel?: string;
  slug: string;
  dubbing?: boolean;
  tts?: {
    [key in TTSModels]?: boolean;
  };
  value: string;
};

function sortLanguagesAndAccents(a: LanguageOrAccent, b: LanguageOrAccent) {
  return a.label.localeCompare(b.label);
}

export const languages: LanguageOrAccent[] = [
  {
    label: "English",
    value: "en",
    slug: "english",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English (USA, UK, Australia, Canada)
  },
  {
    label: "Hindi",
    value: "hi",
    slug: "hindi",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Hindi
  },
  {
    label: "Portuguese",
    // Website `pt` locale is Brazilian Portuguese — surface that in demo pickers.
    displayLabel: "Portuguese (Brazil)",
    value: "pt",
    slug: "portuguese",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Portuguese (Brazil, Portugal)
  },
  {
    label: "Chinese",
    value: "zh",
    slug: "chinese",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Chinese
  },
  {
    label: "Spanish",
    value: "es",
    slug: "spanish",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Spanish (Spain, Mexico)
  },
  {
    label: "French",
    value: "fr",
    slug: "french",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // French (France, Canada)
  },
  {
    label: "German",
    value: "de",
    slug: "german",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // German
  },
  {
    label: "Japanese",
    value: "ja",
    slug: "japanese",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Japanese
  },
  {
    label: "Arabic",
    value: "ar",
    slug: "arabic",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Arabic (Saudi Arabia, UAE)
  },
  {
    label: "Russian",
    value: "ru",
    slug: "russian",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Russian
  },
  {
    label: "Korean",
    value: "ko",
    slug: "korean",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Korean
  },
  {
    label: "Indonesian",
    value: "id",
    slug: "indonesian",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Indonesian
  },
  {
    label: "Italian",
    value: "it",
    slug: "italian",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Italian
  },
  {
    label: "Dutch",
    value: "nl",
    slug: "dutch",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Dutch
  },
  {
    label: "Turkish",
    value: "tr",
    slug: "turkish",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Turkish
  },
  {
    label: "Polish",
    value: "pl",
    slug: "polish",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Polish
  },
  {
    label: "Swedish",
    value: "sv",
    slug: "swedish",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Swedish
  },
  {
    label: "Norwegian",
    value: "no",
    slug: "norwegian",
    asr: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Norwegian
  },
  {
    label: "Filipino",
    value: "tl",
    slug: "filipino",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Filipino
  },
  {
    label: "Malay",
    value: "ms",
    slug: "malay",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Malay
  },
  {
    label: "Romanian",
    value: "ro",
    slug: "romanian",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Romanian
  },
  {
    label: "Hungarian",
    value: "hu",
    slug: "hungarian",
    asr: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Hungarian
  },
  {
    label: "Ukrainian",
    value: "uk",
    slug: "ukrainian",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Ukrainian
  },
  {
    label: "Greek",
    value: "el",
    slug: "greek",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Greek
  },
  {
    label: "Czech",
    value: "cs",
    slug: "czech",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Czech
  },
  {
    label: "Danish",
    value: "da",
    slug: "danish",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Danish
  },
  {
    label: "Finnish",
    value: "fi",
    slug: "finnish",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Finnish
  },
  {
    label: "Bulgarian",
    value: "bg",
    slug: "bulgarian",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Bulgarian
  },
  {
    label: "Croatian",
    value: "hr",
    slug: "croatian",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Croatian
  },
  {
    label: "Slovak",
    value: "sk",
    slug: "slovak",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Slovak
  },
  {
    label: "Tamil",
    value: "ta",
    slug: "tamil",
    asr: true,
    dubbing: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Tamil
  },
  {
    label: "Vietnamese",
    value: "vi",
    slug: "vietnamese",
    asr: true,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Vietnamese
  },
  {
    label: "Afrikaans",
    value: "af",
    slug: "afrikaans",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Amharic",
    value: "am",
    slug: "amharic",
    asr: true,
  },
  {
    label: "Armenian",
    value: "hy",
    slug: "armenian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Assamese",
    value: "as",
    slug: "assamese",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Asturian",
    value: "ast",
    slug: "asturian",
    asr: true,
  },
  {
    label: "Azerbaijani",
    value: "az",
    slug: "azerbaijani",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Bengali",
    value: "bn",
    slug: "bengali",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Belarusian",
    value: "be",
    slug: "belarusian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Bosnian",
    value: "bs",
    slug: "bosnian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  { label: "Burmese", value: "my", slug: "burmese", asr: true },
  {
    label: "Cantonese",
    value: "yue",
    slug: "cantonese",
    asr: true,
  },
  {
    label: "Catalan",
    value: "ca",
    slug: "catalan",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Cebuano",
    value: "ceb",
    slug: "cebuano",
    asr: false,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Central Kurdish",
    value: "ckb",
    slug: "central-kurdish",
    asr: true,
  },
  {
    label: "Chichewa",
    value: "ny",
    slug: "chichewa",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Estonian",
    value: "et",
    slug: "estonian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Fulah",
    value: "ff",
    slug: "fulah",
    asr: true,
  },
  {
    label: "Galician",
    value: "gl",
    slug: "galician",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Ganda",
    value: "lg",
    slug: "ganda",
    asr: true,
  },
  {
    label: "Georgian",
    value: "ka",
    slug: "georgian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Gujarati",
    value: "gu",
    slug: "gujarati",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Hausa",
    value: "ha",
    slug: "hausa",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Hebrew",
    value: "he",
    slug: "hebrew",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Icelandic",
    value: "is",
    slug: "icelandic",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Igbo",
    value: "ig",
    slug: "igbo",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  { label: "Irish", value: "ga", slug: "irish", asr: true, tts: ttsModelsSupportedV3 }, // Not in specified list
  {
    label: "Javanese",
    value: "jv",
    slug: "javanese",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  { label: "Kabuverdianu", value: "kea", slug: "kabuverdianu", asr: true },
  {
    label: "Kannada",
    value: "kn",
    slug: "kannada",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Kazakh",
    value: "kk",
    slug: "kazakh",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Khmer",
    value: "km",
    slug: "khmer",
    asr: true,
  },
  {
    label: "Kirghiz",
    value: "ky",
    slug: "kirghiz",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Lao",
    value: "lo",
    slug: "lao",
    asr: true,
  },
  {
    label: "Latvian",
    value: "lv",
    slug: "latvian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Lingala",
    value: "ln",
    slug: "lingala",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Lithuanian",
    value: "lt",
    slug: "lithuanian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Luo",
    value: "luo",
    slug: "luo",
    asr: true,
  },
  {
    label: "Luxembourgish",
    value: "lb",
    slug: "luxembourgish",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Macedonian",
    value: "mk",
    slug: "macedonian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Maltese",
    value: "mt",
    slug: "maltese",
    asr: true,
  },
  {
    label: "Malayalam",
    value: "ml",
    slug: "malayalam",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Mandarin Chinese",
    value: "cmn",
    slug: "mandarin-chinese",
    asr: false,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Maori",
    value: "mi",
    slug: "maori",
    asr: true,
  },
  {
    label: "Marathi",
    value: "mr",
    slug: "marathi",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Mongolian",
    value: "mn",
    slug: "mongolian",
    asr: true,
  },
  {
    label: "Nepali",
    value: "ne",
    slug: "nepali",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Northern Sotho",
    value: "nso",
    slug: "northern-sotho",
    asr: true,
  },
  {
    label: "Occitan",
    value: "oc",
    slug: "occitan",
    asr: true,
  },
  {
    label: "Oriya",
    value: "or",
    slug: "oriya",
    asr: true,
  },
  {
    label: "Pashto",
    value: "ps",
    slug: "pashto",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Punjabi",
    value: "pa",
    slug: "punjabi",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Pedi",
    value: "nso",
    slug: "pedi",
    asr: true,
  },
  {
    label: "Persian",
    value: "fa",
    slug: "persian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Serbian",
    value: "sr",
    slug: "serbian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Shona",
    value: "sn",
    slug: "shona",
    asr: true,
  },
  {
    label: "Sindhi",
    value: "sd",
    slug: "sindhi",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Slovenian",
    value: "sl",
    slug: "slovenian",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Somali",
    value: "so",
    slug: "somali",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Swahili",
    value: "sw",
    slug: "swahili",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Tajik",
    value: "tg",
    slug: "tajik",
    asr: true,
  },
  {
    label: "Telugu",
    value: "te",
    slug: "telugu",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Thai",
    value: "th",
    slug: "thai",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Umbundu",
    value: "umb",
    slug: "umbundu",
    asr: true,
  },
  {
    label: "Uzbek",
    value: "uz",
    slug: "uzbek",
    asr: true,
  },
  {
    label: "Urdu",
    value: "ur",
    slug: "urdu",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Welsh",
    value: "cy",
    slug: "welsh",
    asr: true,
    tts: ttsModelsSupportedV3, // Not in specified list
  },
  {
    label: "Wolof",
    value: "wo",
    slug: "wolof",
    asr: true,
  },
  {
    label: "Zulu",
    value: "zu",
    slug: "zulu",
    asr: true,
  },
  {
    label: "Xhosa",
    value: "xh",
    slug: "xhosa",
    asr: true,
  },
].sort(sortLanguagesAndAccents);

export const accents: LanguageOrAccent[] = [
  {
    label: "Desi",
    value: "in-desi",
    slug: "desi",
    asr: false,
  },
  {
    label: "Spanish Castilian",
    value: "es-castilian",
    slug: "spanish-castilian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Spanish accent
  },
  {
    label: "Southern USA",
    value: "en-southern-accent",
    slug: "southern-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Texas",
    value: "en-texas-accent",
    slug: "texas-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Country",
    value: "en-country-accent",
    slug: "country-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Cockney",
    value: "en-cockney-accent",
    slug: "cockney-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Latino",
    value: "en-latino-accent",
    slug: "latino-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "American",
    value: "en-american-accent",
    slug: "american-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Filipino",
    value: "en-filipino-accent",
    slug: "filipino-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "French Swiss",
    value: "fr-swiss",
    slug: "french-swiss-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // French accent
  },
  {
    label: "Turkish Istanbul",
    value: "tr-istanbul",
    slug: "turkish-istanbul-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Turkish accent
  },
  {
    label: "Czech Moravian",
    value: "cs-moravian",
    slug: "czech-moravian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Czech accent
  },
  {
    label: "British",
    value: "en-british",
    slug: "british-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Canadian",
    value: "en-canadian",
    slug: "canadian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "New York",
    value: "en-newyork",
    slug: "new-york-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Australian",
    value: "en-australian",
    slug: "australian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Indian",
    value: "en-indian",
    slug: "indian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Irish",
    value: "en-irish",
    slug: "irish-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Italian",
    value: "it-italian",
    slug: "italian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Italian accent
  },
  // temporarily removed requested by Rich Howard
  // {
  //   label: "Essex",
  //   value: "en-essex",
  //   slug: "essex-accent",
  //   asr: false,
  //   tts: ttsModelsSupportedV2nV3,
  // },
  {
    label: "Scandinavian",
    value: "sv-scandinavian",
    slug: "scandinavian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Swedish accent
  },
  {
    label: "German",
    value: "de-german",
    slug: "german-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // German accent
  },
  {
    label: "German Bavarian",
    value: "de-bavarian",
    slug: "german-bavarian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // German accent
  },
  {
    label: "Russian",
    value: "ru-russian",
    slug: "russian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Russian accent
  },
  {
    label: "Spanish",
    value: "es-spanish",
    slug: "spanish-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Spanish accent
  },
  {
    label: "Latin American",
    value: "es-latinamerican",
    slug: "latin-american-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Spanish accent
  },
  {
    label: "Mexican",
    value: "es-mexican",
    slug: "mexican-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Spanish accent
  },
  {
    label: "Argentine",
    value: "es-argentine",
    slug: "argentine-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Spanish accent
  },
  {
    label: "Portuguese",
    value: "pt-portuguese",
    slug: "portuguese-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // Portuguese accent
  },
  {
    label: "Californian",
    value: "en-californian",
    slug: "californian-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "African",
    value: "en-african",
    slug: "african-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
  {
    label: "Pakistani",
    value: "ur-pakistani",
    slug: "pakistani-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3, // Urdu accent - not in specified list
  },
  {
    label: "Scottish",
    value: "en-scottish",
    slug: "scottish-accent",
    asr: false,
    tts: ttsModelsSupportedV2nV3WithTurboV2_5, // English accent
  },
].sort(sortLanguagesAndAccents);

export const accentsMap: Record<string, LanguageOrAccent> = accents.reduce(
  (acc, accent) => {
    acc[accent.value] = accent;
    return acc;
  },
  {} as Record<string, LanguageOrAccent>,
);

export type LanguageMetadata = Pick<LanguageOrAccent, "label" | "slug" | "value" | "tts">;

export const allLanguagesAndAccents = [...languages, ...accents].sort(sortLanguagesAndAccents);

export const ttsLanguages = languages.filter(language => language.tts);
export const ttsAccents = accents.filter(accent => accent.tts);
export const ttsLanguagesAndAccents = [...ttsLanguages, ...ttsAccents].sort(sortLanguagesAndAccents);
export const ttsLanguagesMap: Record<string, LanguageMetadata> = ttsLanguages.reduce(
  (acc, language) => {
    acc[language.value] = language;
    return acc;
  },
  {} as Record<string, LanguageMetadata>,
);

export const asrLanguages = languages.filter(language => language.asr);
export const asrAccents = accents.filter(accent => accent.asr);
export const asrLanguagesAndAccents = [...asrLanguages, ...asrAccents].sort(sortLanguagesAndAccents);

export const dubbingLanguages = languages.filter(language => language.dubbing);

export const convAILanguages = ttsLanguages
  .filter(l => l.tts?.[TTSModels.ELEVEN_MULTILINGUAL_V2])
  .reduce((acc, language) => {
    if (language.value === "pt") {
      // Brazilian must stay first: `getConvAILanguage("pt")` prefix-matches and
      // returns the first `pt-*` entry, so the site's `pt` locale defaults to it.
      acc.push({
        label: "Portuguese (Brazil)",
        value: "pt-BR",
        slug: "portuguese",
      });
      acc.push({
        label: "Portuguese (Portugal)",
        value: "pt-PT",
        slug: "portuguese",
      });
      return acc;
    }
    acc.push(language);
    return acc;
  }, [] as LanguageOrAccent[]);

/**
 * Maps a conversational-AI language value to the code accepted by the agent.
 * `pt-PT` is a UI-only distinction (Portugal flag + label); the agent uses the
 * base `pt` for European Portuguese, while Brazilian keeps `pt-BR`.
 */
export function toAgentLanguageCode(value: string) {
  return value === "pt-PT" ? "pt" : value;
}

/** Single lookup map for all languages and accents by slug */
export const allLanguagesAndAccentsBySlug: Record<string, LanguageOrAccent> = allLanguagesAndAccents.reduce(
  (acc, item) => {
    acc[item.slug] = item;
    return acc;
  },
  {} as Record<string, LanguageOrAccent>,
);
